"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import { ServiceTheme } from "./BookingStepper";

// Brand colors matching BookingStepper theme colors
const themeColors: Record<ServiceTheme, { bg: string; hover: string }> = {
  counseling: { bg: "#896deb", hover: "#7a5de0" },      // Purple
  lifeCoaching: { bg: "#DDA500", hover: "#c99400" },    // Yellow/Gold
  occupationalTherapy: { bg: "#C01610", hover: "#a01210" }, // Red
  psychology: { bg: "#C01610", hover: "#a01210" },      // Red
};

// Default blue theme
const defaultTheme = { bg: "#006DFF", hover: "#0059e6" };

interface BookingNavigationProps {
  onPrevious?: () => void;
  onContinue?: () => void;
  showPrevious?: boolean;
  showContinue?: boolean;
  previousLabel?: string;
  continueLabel?: string;
  continueDisabled?: boolean;
  /** Service theme for brand-colored continue button */
  serviceTheme?: ServiceTheme;
}

const BookingNavigation = ({
  onPrevious,
  onContinue,
  showPrevious = true,
  showContinue = true,
  previousLabel = "Previous",
  continueLabel = "Continue",
  continueDisabled = false,
  serviceTheme,
}: BookingNavigationProps) => {
  // Get theme colors based on serviceTheme prop
  const colors = serviceTheme ? themeColors[serviceTheme] : defaultTheme;

  return (
    <div className="flex items-center justify-between gap-4">
      {/* Previous Button */}
      {showPrevious ? (
        <button
          type="button"
          onClick={onPrevious}
          className="group inline-flex items-center gap-2 rounded-[10px] bg-[#707070] py-1.5 pl-1.5 pr-3 font-onest text-base font-medium text-white transition-all duration-200 ease-out hover:bg-[#5a5a5a] active:scale-[0.98] md:gap-3 md:rounded-[12px] md:py-2 md:pl-2 md:pr-4 md:text-lg"
          style={{
            boxShadow: "inset 0px 0px 8.8px 0px rgba(255,255,255,0.5)",
          }}
          aria-label={previousLabel}
        >
          <span className="flex size-7 items-center justify-center rounded-[6px] bg-white transition-transform duration-200 group-hover:-translate-x-0.5 md:size-[33px] md:rounded-[8px]">
            <ArrowLeft size={14} className="text-[#707070] md:size-4" />
          </span>
          <span className="leading-none">{previousLabel}</span>
        </button>
      ) : (
        <div />
      )}

      {/* Continue Button */}
      {showContinue && (
        <button
          type="button"
          onClick={onContinue}
          disabled={continueDisabled}
          className="group inline-flex items-center gap-2 rounded-[10px] py-1.5 pl-3 pr-1.5 font-onest text-base font-medium text-white transition-all duration-200 ease-out active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 md:gap-3 md:rounded-[12px] md:py-2 md:pl-4 md:pr-2 md:text-lg"
          style={{
            backgroundColor: colors.bg,
            boxShadow: "inset 0px 0px 8.8px 0px rgba(255,255,255,0.5)",
          }}
          onMouseEnter={(e) => {
            if (!continueDisabled) {
              e.currentTarget.style.backgroundColor = colors.hover;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.bg;
          }}
          aria-label={continueLabel}
        >
          <span className="leading-none">{continueLabel}</span>
          <span className="relative flex size-7 items-center justify-center overflow-hidden rounded-[6px] bg-white md:size-[33px] md:rounded-[8px]">
            <ArrowRight
              size={14}
              style={{ color: colors.bg }}
              className="absolute transition-transform duration-200 group-hover:translate-x-[150%] group-disabled:group-hover:translate-x-0 md:size-4"
            />
            <ArrowRight
              size={14}
              style={{ color: colors.bg }}
              className="absolute -translate-x-[150%] transition-transform duration-200 group-hover:translate-x-0 group-disabled:group-hover:-translate-x-[150%] md:size-4"
            />
          </span>
        </button>
      )}
    </div>
  );
};

export default BookingNavigation;
